
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import Toasted from 'vue-toasted';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';
import { WindowType } from '../../../../util';
import { Inventory, InventoryStockTaking, InventoryStockTakingEntry, InventoryUiClient } from '../../../backend/backendapi';
import { initClient } from '../../../../api';
import InventoryDeleteModal from './InventoryDeleteModal';
import InventoryEditModal from './InventoryEditModal/InventoryEditModal';
import InventoryImportModal from './InventoryImportModal';
import InventoryPrintModal from './InventoryPrintModal';
import StockTakingEditModal from '../stockTaking/StockTakingEditModal';

declare var window: WindowType;

Vue.use(Toasted, { duration: 2000 });

@Component({
  name: 'InventoryTab',
  components: {
    InventoryDeleteModal,
    InventoryEditModal,
    InventoryImportModal,
    InventoryPrintModal,
    StockTakingEditModal,
    VueGoodTable,
  },
})
export default class InventoryTab extends Vue {
  //data
  private readonly inventories: Inventory[] = [];

  //runtime
  private readonly selectedInventories: Inventory[] = [];

  //api
  private readonly service = initClient(InventoryUiClient);

  get canBins(): boolean {
    return window.application.permissions.maintenance.inventory.bin.bin;
  };

  get canStockTaking(): boolean {
    return window.application.permissions.maintenance.inventory.stockTaking.management;
  };

  get canUpload(): boolean {
    return window.application.check2.isInventoryUpload;
  };

  get columnsConfig(): any[] {
    return [
      {
        label: 'Ident',
        field: 'ident',
      },
      {
        label: this.$t('txtInvName').toString(),
        field: 'name',
      },
      {
        label: this.$t('txtInvlocation').toString(),
        field: 'location',
      },
      {
        label: this.$t('txtInvOnStock').toString(),
        field: 'itemsOnStock',
      },
      {
        label: this.$t('txtInvSupplier').toString(),
        field: 'supplier',
      },
      {
        label: this.$t('txtInvGeneral').toString(),
        field: (i: Inventory) => i.generalInfo?.substring(0, 12),
      },
    ];
  };

  get paginationConfig(): any {
    return {
      enabled: true,
      nextLabel: this.$t('txtNext'),
      prevLabel: this.$t('txtPrev'),
      rowsPerPageLabel: this.$t('txtPagePer'),
      ofLabel: this.$t('txtOf'),
      pageLabel: this.$t('txtPage'),
      allLabel: this.$t('txtAll'),
      perPage: 30,
    };
  }

  get selectConfig(): any {
    return {
      enabled: true,
      selectOnCheckboxOnly: true, // only select when checkbox is clicked instead of the row
      selectionInfoClass: 'custom-class',
      selectionText: this.$t('comSelectionCnt'),
      clearSelectionText: this.$t('comClear'),
      disableSelectInfo: false, // disable the select info panel on top
      selectAllByGroup: true, // when used in combination with a grouped table, add a checkbox in the header row to check/uncheck the entire group
    };
  }

  mounted() {
    this.fetchSettings();
  }

  fetchSettings(): void {
    this.service.getData(undefined, undefined)
        .then(data => {
          this.inventories.splice(0);
          this.inventories.push(...data);
        });
  }

  clearSelection(): void {
    this.selectedInventories.splice(0);
  }

  createStockTaking(): void {
    if (!this.selectedInventories.length) {
      return;
    }
    const stockTaking = InventoryStockTaking.fromJS({
      id: crypto.randomUUID(),
      name: this.selectedInventories[0].name,
      scheduledAt: new Date(),
      createdAt: new Date(),
      createdBy: window.application.user.id,
      updatedAt: new Date(),
      updatedBy: window.application.user.id,
      entries: this.selectedInventories.map((inventory) => InventoryStockTakingEntry.fromJS({
        id: crypto.randomUUID(),
        inventoryId: inventory.id,
        inventoryIdent: inventory.ident,
        inventoryName: inventory.name,
        createdAt: new Date(),
        createdBy: window.application.user.id,
        updatedAt: new Date(),
        updatedBy: window.application.user.id,
      })),
    });
    (this.$refs.stockTakingEditModal as StockTakingEditModal).open(stockTaking, true);
  }

  downInventory(): void {
    if (!this.selectedInventories.length) {
      return;
    }
    this.service.downInventory(this.selectedInventories)
        .then(_ => this.$toasted.info(this.$t('txtDone').toString()))
        .then(_ => this.fetchAndClear());
  }

  fetchAndClear(): void {
    setTimeout(() => {
      this.fetchSettings();
      this.clearSelection();
    }, 500);
  }

  openAddModal(): void {
    let newInv = Inventory.fromJS({});
    newInv.defaultActionAmount = -1;
    (this.$refs.editModal as InventoryEditModal).open(newInv, false);
  }

  openDeleteModal(): void {
    (this.$refs.deleteModal as InventoryDeleteModal).open(this.selectedInventories);
  }

  openEditModal(): void {
    if (this.selectedInventories.length > 0 && this.selectedInventories[0].id !== undefined) {
      (this.$refs.editModal as InventoryEditModal).open(this.selectedInventories[0], true);
    }
  }

  openImportModal(): void {
    (this.$refs.importModal as InventoryImportModal).open();
  }

  openPrintModal(): void {
    (this.$refs.printModal as InventoryPrintModal).open(this.selectedInventories);
  }

  //events
  onSelectionChanged(params: { selectedRows: Inventory[] }): void {
    this.selectedInventories.splice(0);
    this.selectedInventories.push(...params.selectedRows);
  }
}
